import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
} from 'react';
import ReactDOM from 'react-dom';
import MaskedInput from 'react-text-mask';
import CurrencyInput from 'react-currency-input-field';

import {
  CardType,
  ApiCardField,
  CardCreatePayload,
  CardViewResponse,
  CardSection,
  CardViewField,
} from '../../types/index';
import {
  FieldGroup,
  FieldLabel,
  FieldInput,
  FieldTextarea,
  FieldSelect,
  BooleanOptionsContainer,
  BooleanOptionLabel,
} from '../../styles/index';
import {
  MobileOverlay,
  FullScreenContainer,
  FixedHeader,
  HeaderCloseButton,
  HeaderTitle,
  SwipeHandle,
  ScrollableContent,
  ContentSection,
  ContentSectionTitle,
  SubSectionTitle,
  FixedFooter,
  FooterCancelButton,
  FooterFavoriteButton,
  FooterSaveButton,
  MobileFieldGroup,
  MobileFieldLabel,
  InfoRow,
  InfoLabel,
  InfoValue,
  SavingBar,
  errorInputStyle,
  RequiredAsterisk,
  ClickableReadonlyField,
  FieldErrorMessage,
  DescriptionFieldGroup,
} from './styles';
import refreshToken from '../../../../utils/refreshToken';
import { useToast } from '../../../../hooks/toast';
import { useAuth } from '../../../../hooks/auth';
import useSwipeToClose from '../../../../hooks/useSwipeToClose';
import api from '../../../../services/api';
import { parseKanbanFieldOptions } from '../../utils/parseFieldOptions';

function displaySectionHeading(title: string | null | undefined): string {
  const raw = title ?? '';
  const folded = raw
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
  return folded === 'sem secao' ? 'Outros' : raw;
}

interface ModalCardField {
  id: number;
  label: string;
  field_type: string;
  placeholder: string;
  options?: Array<
    string | number | { label?: string; name?: string; value?: string | number }
  >;
  value: string;
  isEditing: boolean;
  is_required: boolean;
  originalValue: string;
  sectionId: number | null;
  sectionTitle: string;
  sectionPosition: number;
  sectionCollapsed: boolean;
  fieldPosition: number;
  orderIndex: number;
  isLocked?: boolean;
}

interface EditedCardType extends Partial<CardType> {
  isEditingDescription?: boolean;
}

interface SectionGroup {
  sectionId: number | null;
  sectionTitle: string;
  fields: ModalCardField[];
}

interface FieldGroups {
  requiredAndEmpty: SectionGroup[];
  filled: SectionGroup[];
  optionalAndEmpty: SectionGroup[];
}

interface ColumnFieldsApiResponse {
  success: boolean;
  data: { column_id: number; required_fields: ApiCardField[] };
}


export interface MobileCardFullScreenProps {
  isOpen: boolean;
  onClose: () => void;
  card: Partial<CardType>;
  onSave?: (updatedCard: CardType) => void;
  onCreate?: (newCard: CardCreatePayload) => Promise<CardType>;
  boardId: number;
  title?: string;
  className?: string;
}

const FOCUSABLE_SELECTORS =
  'a[href], button:not([disabled]), textarea:not([disabled]), ' +
  'input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

function trapFocus(e: KeyboardEvent, container: HTMLElement) {
  if (e.key !== 'Tab') return;
  const focusable = Array.from(
    container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTORS),
  ).filter(el => el.offsetParent !== null);
  if (!focusable.length) return;
  const first = focusable[0];
  const last = focusable[focusable.length - 1];
  if (e.shiftKey) {
    if (document.activeElement === first) {
      e.preventDefault();
      last.focus();
    }
  } else {
    if (document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }
}

const FieldGroupRenderer = React.memo(
  ({
    field,
    isNewCard,
    onFieldChange,
    onToggleEdit,
    hasError,
    errorMessage,
  }: {
    field: ModalCardField;
    isNewCard: boolean;
    onFieldChange: (fieldId: number, value: string) => void;
    onToggleEdit: (fieldId: number) => void;
    hasError?: boolean;
    errorMessage?: string;
  }) => {
    const inputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
      if (field.isEditing && inputRef.current) inputRef.current.focus();
    }, [field.isEditing]);

    const handleLocalChange = useCallback(
      (
        e: React.ChangeEvent<
          HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
        >,
      ) => {
        onFieldChange(field.id, e.target.value);
      },
      [field.id, onFieldChange],
    );

    const handleBlur = useCallback(() => {
      if (!isNewCard) onToggleEdit(field.id);
    }, [field.id, isNewCard, onToggleEdit]);

    const normalizedType = (field.field_type || '').toLowerCase().trim();
    const normalizedLabel = (field.label || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    const ft = normalizedType;

    const labelSuggestsBoolean =
      normalizedLabel.includes('sim/não') ||
      normalizedLabel.includes('sim/nao') ||
      normalizedLabel.includes('ativo') ||
      normalizedLabel.includes('habilitado');

    const labelSuggestsDateTime =
      normalizedLabel.includes('hora') || normalizedLabel.includes('time');

    const special = (() => {
      if (ft === 'boolean' || ft === 'bool' || labelSuggestsBoolean) return 'boolean';
      if (ft === 'currency' || ft === 'moeda' || normalizedLabel.includes('valor') || normalizedLabel.includes('preco') || normalizedLabel.includes('salario')) return 'currency';
      if (ft === 'percent' || normalizedLabel.includes('percentual') || normalizedLabel.includes('porcentagem')) return 'percent';
      if (ft === 'phone' || ft === 'telefone') return 'phone';
      if (normalizedLabel.includes('cpf') || ft.includes('cpf')) return 'cpf';
      if (normalizedLabel.includes('cnpj') || ft.includes('cnpj')) return 'cnpj';
      if (normalizedLabel.includes('cnh') || ft.includes('cnh')) return 'cnh';
      if (normalizedLabel.includes('rg') || ft.includes('rg')) return 'rg';
      if (normalizedLabel.includes('cep') || ft === 'cep') return 'cep';
      if (ft.includes('datetime') || ft.includes('timestamp')) return 'datetime';
      if ((ft.includes('date') || normalizedLabel.includes('data')) && labelSuggestsDateTime) return 'datetime';
      if (labelSuggestsDateTime) return 'datetime';
      if (ft.includes('date') || normalizedLabel.includes('data')) return 'date';
      return null;
    })();

    const unmaskDigits = (v: string) => v.replace(/\D/g, '');

    const toDateInputValue = (v: string) => {
      if (!v) return '';
      const d = new Date(v);
      if (isNaN(d.getTime())) return v;
      return d.toISOString().split('T')[0];
    };

    const toDateTimeLocalValue = (v: string) => {
      if (!v) return '';
      const d = new Date(v);
      if (isNaN(d.getTime())) return v;
      return d.toISOString().slice(0, 16);
    };

    const fromDateTimeLocalToApi = (v: string) => {
      if (!v) return '';
      return new Date(v).toISOString();
    };

    const rawOpts = parseKanbanFieldOptions(field.options);
    const normalizedOptions: { label: string; value: string }[] = rawOpts
      .map(opt => {
        if (typeof opt === 'object' && opt !== null) {
          const o = opt as any;
          return { label: String(o.label ?? o.name ?? o.value ?? ''), value: String(o.value ?? o.name ?? '') };
        }
        return { label: String(opt), value: String(opt) };
      })
      .filter(opt => opt.value !== '');

    const selectFallbackPlaceholder =
      field.placeholder || 'Digite o valor (opções não carregadas)…';

    const maskedDisplayValue = special === 'phone'
      ? field.value
      : (() => {
          const digits = (field.value || '').replace(/\D/g, '');
          return digits;
        })();

    const renderInput = () => {
      if (ft === 'select' || ft === 'multiselect' || ft === 'radio') {
        if (normalizedOptions.length === 0) {
          return (
            <FieldInput
              value={field.value}
              onChange={handleLocalChange}
              onBlur={handleBlur}
              placeholder={selectFallbackPlaceholder}
              autoFocus={field.isEditing}
              ref={inputRef}
              style={hasError ? errorInputStyle : undefined}
              title="Lista de opções indisponível — use texto livre."
            />
          );
        }
        return (
          <FieldSelect value={field.value} onChange={handleLocalChange} onBlur={handleBlur} style={hasError ? errorInputStyle : undefined}>
            <option value="">{field.placeholder || 'Selecione...'}</option>
            {normalizedOptions.map(opt => (
              <option key={`${field.id}-${opt.value}`} value={opt.value}>{opt.label}</option>
            ))}
          </FieldSelect>
        );
      }

      if (ft === 'textarea') {
        return (
          <FieldTextarea
            value={field.value}
            onChange={handleLocalChange}
            onBlur={handleBlur}
            placeholder={field.placeholder || 'Digite aqui...'}
            autoFocus={field.isEditing}
            ref={inputRef as any}
            style={hasError ? errorInputStyle : undefined}
          />
        );
      }

      if (special === 'boolean') {
        return (
          <BooleanOptionsContainer style={hasError ? errorInputStyle : undefined}>
            <BooleanOptionLabel>
              <input type="radio" name={`bool_${field.id}`} value="1" checked={field.value === '1'} onChange={() => onFieldChange(field.id, '1')} />
              Sim
            </BooleanOptionLabel>
            <BooleanOptionLabel>
              <input type="radio" name={`bool_${field.id}`} value="0" checked={field.value === '0'} onChange={() => onFieldChange(field.id, '0')} />
              Não
            </BooleanOptionLabel>
          </BooleanOptionsContainer>
        );
      }

      if (special === 'currency') {
        return (
          <CurrencyInput
            customInput={FieldInput as any}
            intlConfig={{ locale: 'pt-BR', currency: 'BRL' }}
            value={field.value || ''}
            decimalsLimit={2}
            decimalSeparator=","
            groupSeparator="."
            prefix="R$ "
            onValueChange={value => onFieldChange(field.id, value || '')}
            onBlur={handleBlur}
            placeholder={field.placeholder || '0,00'}
            autoFocus={field.isEditing}
            style={hasError ? errorInputStyle : undefined}
          />
        );
      }

      if (special === 'percent') {
        return (
          <FieldInput
            value={field.value}
            onChange={e => onFieldChange(field.id, e.target.value.replace(/[^0-9,.-]/g, ''))}
            onBlur={handleBlur}
            placeholder={field.placeholder || '0,00%'}
            autoFocus={field.isEditing}
            ref={inputRef}
            style={hasError ? errorInputStyle : undefined}
          />
        );
      }

      if (special === 'datetime') {
        return (
          <FieldInput
            type="datetime-local"
            value={toDateTimeLocalValue(field.value)}
            onChange={e => onFieldChange(field.id, fromDateTimeLocalToApi(e.target.value))}
            onBlur={handleBlur}
            autoFocus={field.isEditing}
            ref={inputRef}
            style={hasError ? errorInputStyle : undefined}
          />
        );
      }

      if (special === 'date') {
        return (
          <FieldInput
            type="date"
            value={toDateInputValue(field.value)}
            onChange={e => onFieldChange(field.id, e.target.value)}
            onBlur={handleBlur}
            autoFocus={field.isEditing}
            ref={inputRef}
            style={hasError ? errorInputStyle : undefined}
          />
        );
      }

      if (special === 'phone' || special === 'cpf' || special === 'cnpj' || special === 'rg' || special === 'cnh' || special === 'cep') {
        let mask: any = null;
        if (special === 'phone') mask = ['(', /\d/, /\d/, ')', ' ', /\d/, /\d/, /\d/, /\d/, /\d/, '-', /\d/, /\d/, /\d/, /\d/];
        else if (special === 'cpf') mask = [/\d/, /\d/, /\d/, '.', /\d/, /\d/, /\d/, '.', /\d/, /\d/, /\d/, '-', /\d/, /\d/];
        else if (special === 'cnpj') mask = [/\d/, /\d/, '.', /\d/, /\d/, /\d/, '.', /\d/, /\d/, /\d/, '/', /\d/, /\d/, /\d/, /\d/, '-', /\d/, /\d/];
        else if (special === 'rg') mask = [/\d/, /\d/, '.', /\d/, /\d/, /\d/, '.', /\d/, /\d/, /\d/, '-', /\d/];
        else if (special === 'cnh') mask = [/\d/, /\d/, /\d/, /\d/, /\d/, /\d/, /\d/, /\d/, /\d/, /\d/, /\d/];
        else if (special === 'cep') mask = [/\d/, /\d/, /\d/, /\d/, /\d/, '-', /\d/, /\d/, /\d/];

        return (
          <MaskedInput
            mask={mask}
            guide={false}
            value={maskedDisplayValue}
            onChange={(e: any) => onFieldChange(field.id, unmaskDigits(e.target.value || ''))}
            onBlur={handleBlur}
            placeholder={field.placeholder || ''}
            render={(ref, props) => (
              <FieldInput ref={ref as any} {...(props as any)} autoFocus={field.isEditing} style={hasError ? errorInputStyle : undefined} />
            )}
          />
        );
      }

      return (
        <FieldInput
          value={field.value}
          onChange={handleLocalChange}
          onBlur={handleBlur}
          placeholder={field.placeholder || 'Digite aqui...'}
          autoFocus={field.isEditing}
          ref={inputRef}
          style={hasError ? errorInputStyle : undefined}
        />
      );
    };

    return (
      <MobileFieldGroup data-field-id={field.id}>
        <MobileFieldLabel>
          {field.label}
          {field.is_required && (
            <RequiredAsterisk aria-hidden="true">*</RequiredAsterisk>
          )}
        </MobileFieldLabel>
        {!isNewCard && !field.isEditing && !field.is_required ? (
          <ClickableReadonlyField
            role="button"
            tabIndex={0}
            aria-label={`Editar campo ${field.label}`}
            onClick={() => onToggleEdit(field.id)}
            onKeyDown={e => (e.key === 'Enter' || e.key === ' ') && onToggleEdit(field.id)}
            $hasError={hasError}
            $empty={!field.value}
          >
            {field.value || <em>Toque para editar…</em>}
          </ClickableReadonlyField>
        ) : (
          renderInput()
        )}
        {hasError && (
          <FieldErrorMessage role="alert">
            {errorMessage || 'Campo obrigatório'}
          </FieldErrorMessage>
        )}
      </MobileFieldGroup>
    );
  },
);

FieldGroupRenderer.displayName = 'MobileFieldGroupRenderer';

function MobileCardFullScreen({
  isOpen,
  onClose,
  card,
  onSave,
  onCreate,
  boardId,
  title,
  className,
}: MobileCardFullScreenProps) {
  const isNewCard = !card?.id;

  const [editedCard, setEditedCard] = useState<EditedCardType>(card);
  const [formFields, setFormFields] = useState<ModalCardField[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [annotation, setAnnotation] = useState('');
  const [fieldErrors, setFieldErrors] = useState<Record<number, string>>({});
  const [hasChanges, setHasChanges] = useState(false);
  const [isAnimatingOut, setIsAnimatingOut] = useState(false);

  const scrollRef = useRef<HTMLElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const previouslyFocused = useRef<HTMLElement | null>(null);

  const { access_token, dateToExpires, user } = useAuth();
  const userId = user?.id;
  const { addToast } = useToast();

  const handleClose = useCallback(() => {
    setIsAnimatingOut(true);
    setTimeout(() => {
      setIsAnimatingOut(false);
      onClose();
    }, 210);
  }, [onClose]);

  const swipeHandlers = useSwipeToClose(handleClose, { threshold: 80 });

  useEffect(() => {
    if (!isOpen) return;

    previouslyFocused.current = document.activeElement as HTMLElement;

    const handleKeydown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        handleClose();
        return;
      }
      if (containerRef.current) trapFocus(e, containerRef.current);
    };

    document.addEventListener('keydown', handleKeydown);

    requestAnimationFrame(() => {
      const first = containerRef.current?.querySelector<HTMLElement>(FOCUSABLE_SELECTORS);
      first?.focus();
    });

    return () => {
      document.removeEventListener('keydown', handleKeydown);
      previouslyFocused.current?.focus();
    };
  }, [isOpen, handleClose]);

  useEffect(() => {
    if (!isOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [isOpen]);

  const normalizeText = useCallback(
    (v: string) =>
      String(v || '')
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .trim(),
    [],
  );

  const isPhaseFieldLabel = useCallback(
    (label: string) => {
      const n = normalizeText(label);
      return n.includes('fase') || n.includes('etapa');
    },
    [normalizeText],
  );

  const currentColumnName = useMemo(
    () =>
      String((editedCard as any)?.column_name || (card as any)?.column_name || '').trim(),
    [editedCard, card],
  );

  const parseBooleanLike = useCallback((value: unknown): boolean | null => {
    if (typeof value === 'boolean') return value;
    if (typeof value === 'number') return value === 1;
    if (typeof value === 'string') {
      const n = value.trim().toLowerCase();
      if (n === '1' || n === 'true' || n === 'sim') return true;
      if (n === '0' || n === 'false' || n === 'nao' || n === 'não') return false;
    }
    return null;
  }, []);

  const getServerFavoriteState = useCallback(
    (raw: any): boolean => {
      const myFav = parseBooleanLike(raw?.myFavorites);
      if (myFav !== null) return myFav;
      const isFav = parseBooleanLike(raw?.is_favorite);
      if (isFav !== null) return isFav;
      if (Array.isArray(raw?.favorites) && userId)
        return raw.favorites.some((f: any) => Number(f?.id) === Number(userId));
      return false;
    },
    [parseBooleanLike, userId],
  );

  const formatDate = (v?: string) => {
    if (!v) return 'N/A';
    try {
      return new Date(v)
        .toLocaleDateString('pt-BR', {
          day: '2-digit', month: '2-digit', year: 'numeric',
          hour: '2-digit', minute: '2-digit',
        })
        .replace(',', ' ');
    } catch {
      return v;
    }
  };

  const orderFieldsByBusinessRule = useCallback((fields: ModalCardField[]): ModalCardField[] => {
    const req: ModalCardField[] = [];
    const filled: ModalCardField[] = [];
    const opt: ModalCardField[] = [];
    fields.forEach(f => {
      const v = String(f.value ?? '').trim();
      if (f.is_required && !v) req.push(f);
      else if (v) filled.push(f);
      else opt.push(f);
    });
    return [...req, ...filled, ...opt];
  }, []);

  const mapTemplateFieldsToModalFields = useCallback(
    (fields: ApiCardField[]): ModalCardField[] => {
      const flattened = fields.map((field, index) => {
        const baseValue = String((field as any)?.value ?? '');
        const isPhase = isPhaseFieldLabel(field.label || '');
        const value = isPhase && currentColumnName ? currentColumnName : baseValue;
        return {
          id: field.id,
          label: field.label,
          field_type: field.field_type,
          placeholder: field.placeholder,
          options: field.options,
          value,
          originalValue: value,
          isEditing: isNewCard ? true : !value.trim().length,
          is_required: isNewCard ? true : Boolean(field.is_required),
          sectionId: null,
          sectionTitle: '',
          sectionPosition: 0,
          sectionCollapsed: false,
          fieldPosition: index,
          orderIndex: index,
          isLocked: isPhase,
        } as ModalCardField;
      });
      return orderFieldsByBusinessRule(flattened);
    },
    [isNewCard, orderFieldsByBusinessRule, isPhaseFieldLabel, currentColumnName],
  );

  const mapSectionsToModalFields = useCallback(
    (sections: CardSection[]): ModalCardField[] => {
      let orderIndex = 0;
      const flattened = [...(sections || [])]
        .sort((a, b) => Number(a.position ?? 0) - Number(b.position ?? 0))
        .flatMap(section => {
          const sectionTitle = section?.title ?? '';
          return [...(section?.fields || [])]
            .filter(f => !f.hidden)
            .sort((a, b) => Number(a.position ?? 0) - Number(b.position ?? 0))
            .map((field: CardViewField) => {
              const isPhase = isPhaseFieldLabel(field.label || '');
              const baseValue = String(field.value ?? '');
              const value = isPhase && currentColumnName ? currentColumnName : baseValue;
              return {
                id: field.id,
                label: field.label,
                field_type: field.field_type,
                placeholder: field.placeholder,
                options: field.options,
                value,
                originalValue: value,
                isEditing: isNewCard ? true : !value.trim().length,
                is_required: Boolean(field.required),
                sectionId: section?.id ?? null,
                sectionTitle,
                sectionPosition: Number(section?.position ?? 0),
                sectionCollapsed: Boolean(section?.collapsed),
                fieldPosition: Number(field.position ?? 0),
                orderIndex: orderIndex++,
                isLocked: isPhase,
              } as ModalCardField;
            });
        });
      return orderFieldsByBusinessRule(flattened);
    },
    [isNewCard, orderFieldsByBusinessRule, isPhaseFieldLabel, currentColumnName],
  );

  const extractColumnTemplateFields = useCallback((raw: any): ApiCardField[] => {
    const root = raw?.data ?? raw;
    const candidates = [
      root?.required_fields,
      raw?.required_fields,
      root?.fields,
      raw?.fields,
    ];
    for (const arr of candidates) {
      if (Array.isArray(arr) && arr.length > 0) {
        return arr.map((f: any) => ({
          ...f,
          is_required: Boolean(f?.is_required ?? f?.required),
          columns: Array.isArray(f?.columns) ? f.columns : [],
          required_column: f?.required_column ?? null,
        })) as ApiCardField[];
      }
    }
    const sections = Array.isArray(root?.sections) ? root.sections : Array.isArray(raw?.sections) ? raw.sections : [];
    if (sections.length > 0) {
      return sections.flatMap((s: any) =>
        (Array.isArray(s?.fields) ? s.fields : []).map((f: any) => ({
          id: Number(f?.id),
          label: String(f?.label ?? ''),
          field_type: String(f?.field_type ?? 'text'),
          placeholder: String(f?.placeholder ?? ''),
          options: f?.options,
          is_required: Boolean(f?.is_required ?? f?.required),
          columns: [],
          required_column: null,
        } as ApiCardField)),
      );
    }
    return [];
  }, []);

  useEffect(() => {
    let isMounted = true;

    if (isOpen) {
      (async () => {
        try {
          const token = await refreshToken(dateToExpires, access_token);
          api.defaults.headers.authorization = `Bearer ${token || access_token}`;

          setEditedCard(card);

          let processedFields: ModalCardField[] = [];
          let cardToEdit: Partial<CardType> = card;
          let currentAnnotation = (card as any)?.annotation || '';

          if (isNewCard) {
            const colId = card?.column_id;
            if (colId == null) {
              addToast({ type: 'error', title: 'Erro', description: 'Coluna não identificada' });
              return;
            }
            const resp = await api.get(`/api/adm/columns/${colId}/required-fields`);
            const apiData: ColumnFieldsApiResponse = resp.data;
            const templateFields = extractColumnTemplateFields({ required_fields: apiData?.data?.required_fields || [] });
            processedFields = mapTemplateFieldsToModalFields(templateFields);
          } else if (card?.id) {
            const cardResp = await api.get(`/api/adm/cards/${card.id}/view`);
            const data: CardViewResponse = cardResp.data;
            const cardData = data?.card;
            const sections = data?.sections || [];
            if (cardData) {
              cardToEdit = { ...(card as any), ...cardData, myFavorites: getServerFavoriteState(card) };
              currentAnnotation = cardData.annotation || '';
            }
            processedFields = mapSectionsToModalFields(sections);
          }

          if (isMounted) {
            setEditedCard(cardToEdit as EditedCardType);
            setFormFields(processedFields);
            setAnnotation(currentAnnotation);
            setFieldErrors({});
            setHasChanges(false);

            requestAnimationFrame(() => {
              try {
                if (scrollRef.current) scrollRef.current.scrollTop = 0;
              } catch (_) {}
            });
          }
        } catch (_) {
          if (isMounted) addToast({ type: 'error', title: 'Erro ao carregar dados' });
        }
      })();
    }

    return () => { isMounted = false; };
  }, [
    isOpen, card, isNewCard, access_token, dateToExpires,
    addToast, boardId,
    mapTemplateFieldsToModalFields, mapSectionsToModalFields,
    extractColumnTemplateFields, getServerFavoriteState,
  ]);

  const fieldGroups = useMemo<FieldGroups>(() => {
    const req: ModalCardField[] = [];
    const filled: ModalCardField[] = [];
    const opt: ModalCardField[] = [];

    formFields.forEach(f => {
      const persisted = String(f.originalValue ?? '').trim();
      if (f.is_required && !persisted) req.push(f);
      else if (persisted) filled.push(f);
      else opt.push(f);
    });

    const toGroups = (fields: ModalCardField[]): SectionGroup[] => {
      const map = new Map<string, SectionGroup>();
      fields.forEach(f => {
        const key = `${f.sectionId ?? 'no'}::${f.sectionTitle ?? ''}`;
        if (!map.has(key)) map.set(key, { sectionId: f.sectionId, sectionTitle: f.sectionTitle ?? '', fields: [] });
        map.get(key)!.fields.push(f);
      });
      return Array.from(map.values());
    };

    return { requiredAndEmpty: toGroups(req), filled: toGroups(filled), optionalAndEmpty: toGroups(opt) };
  }, [formFields]);

  const effectiveHasChanges = useMemo(() => {
    const fieldsDirty = formFields.some(f => (f.value || '') !== (f.originalValue || ''));
    const descDirty = (editedCard?.description || '') !== (card?.description || '');
    const prioDirty = (editedCard?.priority || '') !== (card?.priority || '');
    const favDirty = (editedCard as any)?.myFavorites !== (card as any)?.myFavorites;
    const annDirty = (annotation || '') !== ((card as any)?.annotation || '');
    return hasChanges || fieldsDirty || descDirty || prioDirty || favDirty || annDirty;
  }, [formFields, editedCard, card, hasChanges, annotation]);

  const handleFieldChange = useCallback((fieldId: number, value: string) => {
    setFormFields(prev => prev.map(f => f.id !== fieldId ? f : f.isLocked ? f : { ...f, value }));
    setFieldErrors(prev => {
      if (!prev[fieldId]) return prev;
      if (String(value ?? '').trim()) { const n = { ...prev }; delete n[fieldId]; return n; }
      return prev;
    });
  }, []);

  const toggleFieldEdit = useCallback((fieldId: number) => {
    setFormFields(prev => prev.map(f => f.id === fieldId ? { ...f, isEditing: !f.isEditing } : f));
  }, []);

  const handleDescriptionChange = (value: string) => {
    setEditedCard(prev => ({ ...prev, description: value }));
    setHasChanges(true);
  };

  const handlePriorityChange = (value: string) => {
    setEditedCard(prev => ({ ...prev, priority: value as CardType['priority'] }));
    setHasChanges(true);
  };

  const handleFavoriteToggle = () => {
    setEditedCard(prev => ({ ...prev, myFavorites: !prev.myFavorites }));
    setHasChanges(true);
  };

  const handleSave = async () => {
    if (isNewCard && !onCreate) return;
    if (!isNewCard && !onSave) return;

    const emptyRequired = formFields.filter(f => f.is_required && !String(f.value ?? '').trim());
    if (emptyRequired.length > 0) {
      const errors = emptyRequired.reduce<Record<number, string>>((acc, f) => {
        acc[f.id] = 'Campo obrigatório não preenchido';
        return acc;
      }, {});
      setFieldErrors(errors);
      requestAnimationFrame(() => {
        const target = containerRef.current?.querySelector<HTMLElement>(`[data-field-id="${emptyRequired[0].id}"]`);
        target?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      });
      addToast({
        type: 'error',
        title: 'Campos obrigatórios',
        description: `Preencha: ${emptyRequired.map(f => f.label).join(', ')}`,
      });
      return;
    }

    setFieldErrors({});
    setIsSaving(true);

    try {
      const token = await refreshToken(dateToExpires, access_token);
      api.defaults.headers.authorization = `Bearer ${token || access_token}`;

      const normalizeValue = (v: unknown) => (v == null ? '' : String(v).trim());
      const cardFieldsCreate = formFields.map(({ id, value }) => ({ card_field_id: id, value: normalizeValue(value) })).filter(f => f.value !== '');
      const cardFieldsUpdate = formFields.map(({ id, value }) => ({ id, value: normalizeValue(value) }));

      if (isNewCard) {
        if (!editedCard.column_id) return;
        const newCardPayload: CardCreatePayload = {
          description: editedCard.description || '',
          position: 1,
          column_id: editedCard.column_id,
          card_fields: cardFieldsCreate,
        };
        const created = await onCreate!(newCardPayload);
        onSave?.(created);
        addToast({ type: 'success', title: 'Card criado com sucesso!' });
        handleClose();
      } else {
        const cardId = Number(editedCard.id);
        const basePayload = {
          description: editedCard.description || '',
          annotation: annotation || '',
          priority: editedCard.priority || 'important',
          card_fields: cardFieldsUpdate,
        };

        await api.put(`/api/adm/cards/${cardId}`, basePayload);

        let finalFav = !!editedCard.myFavorites;
        const prevFav = !!(card as CardType).myFavorites;
        if (finalFav !== prevFav && userId) {
          const url = `/api/adm/cards/${cardId}/favorite?userId=${userId}`;
          try {
            finalFav ? await api.post(url) : await api.delete(url);
          } catch (favErr: any) {
            if (favErr?.response?.status === 409 && finalFav) finalFav = true;
            else throw favErr;
          }
        }

        let refreshedCard: Partial<CardType> | null = null;
        try {
          const r = await api.get(`/api/adm/cards/${cardId}`);
          const raw = r?.data?.data || r?.data || null;
          if (raw) refreshedCard = { ...(raw as any), myFavorites: getServerFavoriteState(raw) };
        } catch (_) {}

        setFormFields(prev => prev.map(f => ({ ...f, originalValue: f.value, isEditing: false })));
        setHasChanges(false);

        const updated: CardType = {
          ...(editedCard as CardType),
          ...(refreshedCard as any),
          myFavorites: refreshedCard ? getServerFavoriteState(refreshedCard) : finalFav,
          annotation: refreshedCard ? (refreshedCard as any)?.annotation : annotation || null,
          card_fields: formFields.map(f => ({ ...f, isEditing: false })),
        };

        onSave!(updated);
        addToast({ type: 'success', title: 'Card atualizado!' });
        handleClose();
      }
    } catch (err: any) {
      addToast({
        type: 'error',
        title: 'Erro ao salvar',
        description: err?.response?.data?.message || 'Tente novamente mais tarde',
      });
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen && !isAnimatingOut) return null;

  const isFavorite = !!(editedCard as any)?.myFavorites;
  const headerTitle = title || editedCard?.description || 'Card';

  const renderSectionGroups = (groups: SectionGroup[], groupColor?: string) =>
    groups.map(section => (
      <div key={`${section.sectionId ?? 'no'}-${section.sectionTitle}`}>
        <SubSectionTitle>{displaySectionHeading(section.sectionTitle)}</SubSectionTitle>
        {section.fields.map(field => (
          <FieldGroupRenderer
            key={`${section.sectionId ?? 'no'}-${field.id}`}
            field={field}
            isNewCard={isNewCard}
            onFieldChange={handleFieldChange}
            onToggleEdit={toggleFieldEdit}
            hasError={Boolean(fieldErrors[field.id])}
            errorMessage={fieldErrors[field.id]}
          />
        ))}
      </div>
    ));

  const node = (
    <>
      <MobileOverlay
        isAnimatingOut={isAnimatingOut}
        onClick={handleClose}
        aria-hidden="true"
      />

      <FullScreenContainer
        ref={containerRef}
        isAnimatingOut={isAnimatingOut}
        className={className}
        role="dialog"
        aria-modal="true"
        aria-label={headerTitle}
        {...swipeHandlers}
      >
        {isSaving && <SavingBar aria-hidden="true" />}

        <FixedHeader>
          <SwipeHandle aria-hidden="true" />
          <HeaderCloseButton
            onClick={handleClose}
            aria-label="Fechar"
            type="button"
          >
            &times;
          </HeaderCloseButton>
          <HeaderTitle aria-level={2}>{headerTitle}</HeaderTitle>
        </FixedHeader>

        <ScrollableContent ref={scrollRef as any} aria-label="Conteúdo do card">

          <ContentSection>
            <DescriptionFieldGroup>
              <FieldLabel>Descrição</FieldLabel>
              <FieldInput
                value={editedCard.description || ''}
                onChange={e => handleDescriptionChange(e.target.value)}
                placeholder="Digite a descrição..."
                aria-label="Descrição do card"
              />
            </DescriptionFieldGroup>
          </ContentSection>

          {fieldGroups.requiredAndEmpty.length > 0 && (
            <ContentSection>
              <ContentSectionTitle>
                Campos Obrigatórios ({fieldGroups.requiredAndEmpty.reduce((t, s) => t + s.fields.length, 0)})
              </ContentSectionTitle>
              {renderSectionGroups(fieldGroups.requiredAndEmpty)}
            </ContentSection>
          )}

          {fieldGroups.filled.length > 0 && (
            <ContentSection>
              <ContentSectionTitle>
                Campos Preenchidos ({fieldGroups.filled.reduce((t, s) => t + s.fields.length, 0)})
              </ContentSectionTitle>
              {renderSectionGroups(fieldGroups.filled)}
            </ContentSection>
          )}

          {fieldGroups.optionalAndEmpty.length > 0 && (
            <ContentSection>
              <ContentSectionTitle>
                Campos Opcionais ({fieldGroups.optionalAndEmpty.reduce((t, s) => t + s.fields.length, 0)})
              </ContentSectionTitle>
              {renderSectionGroups(fieldGroups.optionalAndEmpty)}
            </ContentSection>
          )}

          <ContentSection>
            <ContentSectionTitle>Prioridade</ContentSectionTitle>
            <FieldSelect
              value={editedCard.priority || 'important'}
              onChange={e => handlePriorityChange(e.target.value)}
              aria-label="Prioridade do card"
            >
              <option value="urgent">Urgente</option>
              <option value="important">Importante</option>
              <option value="not important">Não importante</option>
            </FieldSelect>
          </ContentSection>

          <ContentSection>
            <ContentSectionTitle>Anotações</ContentSectionTitle>
            <FieldTextarea
              value={annotation}
              onChange={e => {
                setAnnotation(e.target.value);
                setHasChanges(true);
              }}
              placeholder="Adicionar anotação..."
              rows={3}
              aria-label="Anotações"
            />
          </ContentSection>

          {!isNewCard && (
            <ContentSection>
              <ContentSectionTitle>Datas</ContentSectionTitle>
              <InfoRow>
                <div>
                  <InfoLabel>Criação</InfoLabel>
                  <InfoValue>
                    {formatDate(
                      (editedCard as any)?.created_at ||
                      (editedCard as any)?.createdAt ||
                      (card as any)?.created_at ||
                      (card as any)?.createdAt,
                    )}
                  </InfoValue>
                </div>
                <div>
                  <InfoLabel>Atualização</InfoLabel>
                  <InfoValue>
                    {formatDate(
                      (editedCard as any)?.updated_at ||
                      (editedCard as any)?.updatedAt ||
                      (card as any)?.updated_at ||
                      (card as any)?.updatedAt,
                    )}
                  </InfoValue>
                </div>
              </InfoRow>
            </ContentSection>
          )}
        </ScrollableContent>

        <FixedFooter>
          <FooterSaveButton
            type="button"
            onClick={handleSave}
            disabled={isSaving}
            aria-label={isSaving ? 'Salvando…' : 'Salvar alterações'}
            aria-busy={isSaving}
          >
            {isSaving ? 'Salvando…' : 'Salvar'}
          </FooterSaveButton>

          <FooterCancelButton
            type="button"
            onClick={handleClose}
            aria-label="Cancelar e fechar"
          >
            Cancelar
          </FooterCancelButton>

          {!isNewCard && (
            <FooterFavoriteButton
              type="button"
              isFavorite={isFavorite}
              onClick={handleFavoriteToggle}
              aria-label={isFavorite ? 'Remover dos favoritos' : 'Adicionar aos favoritos'}
              aria-pressed={isFavorite}
            >
              {isFavorite ? '❤️' : '🤍'}
            </FooterFavoriteButton>
          )}
        </FixedFooter>
      </FullScreenContainer>
    </>
  );

  return ReactDOM.createPortal(node, document.body);
}

export default MobileCardFullScreen;
